// App Configuration
export const APP_CONFIG = {
  NAME: "HARVN",
  VERSION: "1.0.0",
  DESCRIPTION: "Self-Reflection. Clarity. Personal Growth.",
  COMPANY: "HARVN",
  CONTACT_EMAIL: "support@harvn.com",
  WEBSITE: "https://harvn.com",
} as const;

// API Configuration
// Note: BASE_URL is defined in api.ts to avoid import.meta issues in tests
export const API_CONFIG = {
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
} as const;

// Authentication
export const AUTH_CONFIG = {
  TOKEN_KEY: "harvn-token",
  REFRESH_TOKEN_KEY: "harvn-refresh-token",
  SESSION_TIMEOUT: 24 * 60 * 60 * 1000, // 24 hours
  REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutes
} as const;

// Subscription Plans
export const SUBSCRIPTION_PLANS = {
  FREEMIUM: {
    id: "freemium",
    name: "Freemium",
    price: 0,
    features: ["Limited chat sessions", "Basic questionnaire", "Email support"],
    limits: {
      chatMessages: 50,
      chatSessions: 3,
      fileUploads: 0,
    },
  },
  FULL_ACCESS: {
    id: "full_access",
    name: "Full Access",
    price: 19.99,
    features: [
      "Unlimited chat sessions",
      "Complete questionnaire",
      "Chat history",
      "File uploads",
      "Priority support",
    ],
    limits: {
      chatMessages: -1, // unlimited
      chatSessions: -1, // unlimited
      fileUploads: 10,
    },
  },
  PRO: {
    id: "pro",
    name: "Pro",
    price: 39.99,
    features: [
      "Everything in Full Access",
      "Advanced AI features",
      "Custom themes",
      "Priority processing",
      "Dedicated support",
    ],
    limits: {
      chatMessages: -1, // unlimited
      chatSessions: -1, // unlimited
      fileUploads: 50,
    },
  },
} as const;

// Questionnaire Configuration
export const QUESTIONNAIRE_CONFIG = {
  TOTAL_QUESTIONS: 9,
  MAX_ANSWER_LENGTH: 2000,
  MIN_ANSWER_LENGTH: 10,
  AUTO_SAVE_INTERVAL: 30000, // 30 seconds
  INACTIVITY_TIMEOUT: 600000, // 10 minutes
  RETRY_ATTEMPTS: 3,
} as const;

// Chat Configuration
export const CHAT_CONFIG = {
  MAX_MESSAGE_LENGTH: 4000,
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  SUPPORTED_FILE_TYPES: [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
  ],
  TYPING_INDICATOR_TIMEOUT: 3000,
  MESSAGE_BATCH_SIZE: 50,
} as const;

// Theme Configuration
export const THEME_CONFIG = {
  DEFAULT_THEME: "life" as const,
  STORAGE_KEY: "harvn-theme",
  THEMES: {
    LIFE: "life",
    PRO: "pro",
  },
} as const;

// Language Configuration
export const LANGUAGE_CONFIG = {
  DEFAULT_LANGUAGE: "en" as const,
  STORAGE_KEY: "harvn-language",
  SUPPORTED_LANGUAGES: [
    { code: "en", name: "English", nativeName: "English" },
    { code: "fr", name: "French", nativeName: "Français" },
    { code: "it", name: "Italian", nativeName: "Italiano" },
    { code: "de", name: "German", nativeName: "Deutsch" },
    { code: "es", name: "Spanish", nativeName: "Español" },
  ],
} as const;

// Validation Rules
export const VALIDATION_RULES = {
  EMAIL: {
    PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    MAX_LENGTH: 254,
  },
  PASSWORD: {
    MIN_LENGTH: 8,
    MAX_LENGTH: 128,
    REQUIRE_UPPERCASE: true,
    REQUIRE_LOWERCASE: true,
    REQUIRE_NUMBER: true,
    REQUIRE_SPECIAL: false,
  },
  NAME: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 100,
  },
  CHAT_MESSAGE: {
    MIN_LENGTH: 1,
    MAX_LENGTH: CHAT_CONFIG.MAX_MESSAGE_LENGTH,
  },
  QUESTIONNAIRE_ANSWER: {
    MIN_LENGTH: QUESTIONNAIRE_CONFIG.MIN_ANSWER_LENGTH,
    MAX_LENGTH: QUESTIONNAIRE_CONFIG.MAX_ANSWER_LENGTH,
  },
} as const;

// Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: AUTH_CONFIG.TOKEN_KEY,
  REFRESH_TOKEN: AUTH_CONFIG.REFRESH_TOKEN_KEY,
  THEME: THEME_CONFIG.STORAGE_KEY,
  LANGUAGE: LANGUAGE_CONFIG.STORAGE_KEY,
  QUESTIONNAIRE_PROGRESS: "harvn-questionnaire-progress",
  CHAT_DRAFT: "harvn-chat-draft",
  USER_PREFERENCES: "harvn-user-preferences",
} as const;

// API Endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: "/auth/login",
    REGISTER: "/auth/register",
    LOGOUT: "/auth/logout",
    REFRESH: "/auth/refresh",
    FORGOT_PASSWORD: "/auth/forgot-password",
    RESET_PASSWORD: "/auth/reset-password",
    VERIFY_EMAIL: "/auth/verify-email",
  },
  USER: {
    PROFILE: "/user/profile",
    SETTINGS: "/user/settings",
    DELETE: "/user/delete",
  },
  SUBSCRIPTION: {
    PLANS: "/subscription/plans",
    SUBSCRIBE: "/subscription/subscribe",
    CANCEL: "/subscription/cancel",
    UPDATE: "/subscription/update",
    STATUS: "/subscription/status",
  },
  QUESTIONNAIRE: {
    QUESTIONS: "/questionnaire/questions",
    ANSWERS: "/questionnaire/answers",
    PROGRESS: "/questionnaire/progress",
    SUBMIT: "/questionnaire/submit",
  },
  CHAT: {
    STREAM: "/api/v1/chat/stream",
    NON_STREAMING: "/api/v1/chat/",
    PII_DETECT: "/api/v1/chat/pii-detect",
    SESSIONS: "/api/v1/chat-sessions/",
    CLOSE_SESSION: "/api/v1/chat/close-session",
    CLOSE_ALL_SESSIONS: "/api/v1/chat/close-all-sessions",
    ACTIVE_SESSIONS: "/api/v1/chat/active-sessions",
    UPLOAD: "/api/v1/files/upload",
  },
  FAQ: {
    LIST: "/faq",
    SEARCH: "/faq/search",
  },
  STRIPE: {
    CREATE_CHECKOUT: "/stripe/create-checkout-session",
    WEBHOOK: "/stripe/webhook",
    CUSTOMER_PORTAL: "/stripe/customer-portal",
  },
} as const;

// Error Codes
export const ERROR_CODES = {
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  SERVER_ERROR: "SERVER_ERROR",
  NETWORK_ERROR: "NETWORK_ERROR",
  TIMEOUT: "TIMEOUT",
  SUBSCRIPTION_EXPIRED: "SUBSCRIPTION_EXPIRED",
  SUBSCRIPTION_LIMIT_REACHED: "SUBSCRIPTION_LIMIT_REACHED",
  QUESTIONNAIRE_INCOMPLETE: "QUESTIONNAIRE_INCOMPLETE",
  FILE_TOO_LARGE: "FILE_TOO_LARGE",
  FILE_TYPE_NOT_SUPPORTED: "FILE_TYPE_NOT_SUPPORTED",
  ERROR_VOICE_NO_SPEECH_DETECTED: "ERROR_VOICE_NO_SPEECH_DETECTED",
  CONFLICT: "CONFLICT",
} as const;

// Local Storage Keys
export const LOCAL_STORAGE_KEYS = {
  ...STORAGE_KEYS,
  ONBOARDING_COMPLETED: "harvn-onboarding-completed",
  TERMS_ACCEPTED: "harvn-terms-accepted",
  PRIVACY_ACCEPTED: "harvn-privacy-accepted",
  FIRST_VISIT: "harvn-first-visit",
} as const;

// Route Paths
export const ROUTES = {
  HOME: "/",
  LOGIN: "/login",
  SIGNUP: "/signup",
  REGISTER: "/register",
  FORGOT_PASSWORD: "/forgot-password",
  RESET_PASSWORD: "/reset-password",
  VERIFY_EMAIL: "/verify-email",
  ONBOARDING: "/onboarding",
  QUESTIONNAIRE: "/questionnaire",
  CHAT: "/chat",
  CHAT_SESSION: "/chat/:sessionId", // Route pattern for specific chat sessions
  HISTORY: "/history",
  SETTINGS: "/settings",
  PROFILE: "/profile",
  FAQ: "/faq",
  SUBSCRIPTION: "/subscription",
  TERMS: "/terms",
  PRIVACY: "/privacy",
  SUPPORT: "/support",
  NOT_FOUND: "/404",
} as const;

// UI Constants
export const UI_CONFIG = {
  TOAST_DURATION: 5000,
  MODAL_ANIMATION_DURATION: 300,
  LOADING_SPINNER_DELAY: 200,
  DEBOUNCE_DELAY: 300,
  THROTTLE_DELAY: 100,
} as const;
